import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import type { Permission } from '@/auth/permissions';

/**
 * The route-level gate. The nav already hides what a role cannot reach, but a
 * pasted URL must not get further than the nav does — and it must land
 * somewhere that explains itself rather than on an empty screen.
 *
 * **Signed out is not the same as unauthorised**, and the difference is the
 * whole of what the visitor should do next. A visitor with no session is sent
 * to `/login`; `/no-access` is for somebody who authenticated and holds no
 * permission, and telling a signed-out visitor that no role has been granted
 * to them is a support call about a problem they do not have.
 */
export function RequirePermission({
  anyOf,
  children,
}: {
  anyOf: Permission[];
  children: ReactNode;
}) {
  const { has, status } = useAuth();
  if (status === 'loading') return null;
  if (status !== 'signed-in') return <Navigate to="/login" replace />;
  if (!anyOf.some(has)) return <Navigate to="/no-access" replace />;
  return <>{children}</>;
}
